import { EmptyState } from "@/components/molecules/empty-state";
import { ErrorState } from "@/components/molecules/error-state";
import type { BulkAction } from "@/components/molecules/tables/bulk-actions";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useQualifications } from "@/features/qualifications/hooks/use-qualifications";
import type { Qualification } from "@/features/qualifications/types";
import { useDebounce } from "@/features/utils/use-debounce";
import { useDateFnsLocale, useTranslation } from "@/features/utils/i18n";
import {
  ClipboardTextIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  XIcon,
} from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { format, isThisYear, isToday, isYesterday } from "date-fns";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useQualificationsBulkActions } from "./bulk-actions";
import {
  activeFilterCount,
  DEFAULT_FILTERS,
  QualificationsFilters,
  SortMenu,
  toQuery,
  type SubmissionFilters,
  type SubmissionSort,
} from "./filters";
import { SubmissionListItem, submittedAt } from "./submission-list-item";

const PAGE_SIZE = 30;

/**
 * The list's query and selection, held by the page rather than by the list: the
 * detail pane steps to the previous and next submission *of this list*, in its
 * order and under its filters, so both panes read the same rows.
 */
export function useSubmissionList() {
  const [search, setSearch] = useState("");
  const [filters, setFiltersState] =
    useState<SubmissionFilters>(DEFAULT_FILTERS);
  const [sort, setSortState] = useState<SubmissionSort>("score");
  const [limit, setLimit] = useState(PAGE_SIZE);
  // Checked rows, kept whole: a row checked and then filtered out of view is
  // still acted on as it was when it was checked.
  const [checked, setChecked] = useState<Map<string, Qualification>>(
    () => new Map(),
  );
  // Typing filters the list, so the request waits for the typist to stop.
  const debouncedSearch = useDebounce(search, 300);

  const query = useQualifications(
    {
      ...toQuery(filters, sort),
      search: debouncedSearch || undefined,
      limit,
    },
    { keepPrevious: true },
  );

  const items = useMemo(() => query.data?.data || [], [query.data]);

  // A new cut of the list is a new list: what was checked in the old one goes.
  const setFilters = (next: SubmissionFilters) => {
    setFiltersState(next);
    setChecked(new Map());
    setLimit(PAGE_SIZE);
  };
  const setSort = (next: SubmissionSort) => {
    setSortState(next);
    setLimit(PAGE_SIZE);
  };

  return {
    search,
    setSearch,
    filters,
    setFilters,
    sort,
    setSort,
    filtering: activeFilterCount(filters) > 0 || !!debouncedSearch,
    items,
    total: query.data?.total || 0,
    hasMore: query.data?.has_more || items.length < (query.data?.total || 0),
    loadMore: () => setLimit((current) => current + PAGE_SIZE),
    query,
    checked,
    check: (qualification: Qualification, on: boolean) =>
      setChecked((current) => {
        const next = new Map(current);
        if (on) next.set(qualification.id, qualification);
        else next.delete(qualification.id);
        return next;
      }),
    checkAll: (on: boolean) =>
      setChecked(
        on ? new Map(items.map((item) => [item.id, item])) : new Map(),
      ),
    clearChecked: () => setChecked(new Map()),
  };
}

export type SubmissionListState = ReturnType<typeof useSubmissionList>;

/**
 * The left pane: search, filters, sort, the submissions — and the boxes that
 * make a group of them one decision.
 */
export function SubmissionList({
  list,
  selectedId,
  onSelect,
  onCreate,
}: {
  list: SubmissionListState;
  selectedId?: string;
  onSelect: (qualification: Qualification) => void;
  onCreate: () => void;
}) {
  const { t } = useTranslation("qualifications");
  const { items, query, checked } = list;
  const bulkActions = useQualificationsBulkActions();
  const byDate = list.sort === "recent" || list.sort === "oldest";

  // Stepping through the detail pane can walk past the visible rows: keep the
  // selected one in view.
  useEffect(() => {
    if (!selectedId) return;
    document
      .querySelector(`[data-submission-id="${CSS.escape(selectedId)}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [selectedId]);

  const allChecked =
    items.length > 0 && items.every((item) => checked.has(item.id));
  const someChecked = checked.size > 0;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="space-y-2 border-b p-3">
        <div className="flex items-center gap-2">
          <div className="relative min-w-0 flex-1">
            <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={list.search}
              onChange={(e) => list.setSearch(e.target.value)}
              placeholder={t("submissions.list.search")}
              className="pl-8 pr-8"
            />
            {list.search && (
              <button
                type="button"
                onClick={() => list.setSearch("")}
                aria-label={t("submissions.list.clear_search")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <XIcon className="h-4 w-4" />
              </button>
            )}
          </div>
          <Button
            size="icon"
            onClick={onCreate}
            aria-label={t("submissions.add")}
            title={t("submissions.add")}
          >
            <PlusIcon className="size-4" />
          </Button>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <QualificationsFilters
            filters={list.filters}
            onFiltersChange={list.setFilters}
          />
        </div>
      </div>

      {someChecked && (
        <SelectionBar
          rows={[...checked.values()]}
          actions={bulkActions}
          onClear={list.clearChecked}
        />
      )}

      <div className="flex items-center gap-2 border-b py-1.5 pr-2 pl-3">
        <Checkbox
          checked={allChecked ? true : someChecked ? "indeterminate" : false}
          onCheckedChange={(value) => list.checkAll(value === true)}
          aria-label={t("submissions.list.select_all")}
          disabled={items.length === 0}
        />
        <span className="flex-1 text-xs text-muted-foreground">
          {t("submissions.list.count", { count: list.total })}
        </span>
        <SortMenu value={list.sort} onChange={list.setSort} />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {query.isLoading ? (
          <div className="space-y-4 p-3">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="space-y-2">
                <Skeleton className="h-4 w-1/2" />
                <Skeleton className="h-3 w-3/4" />
                <Skeleton className="h-3 w-1/3" />
              </div>
            ))}
          </div>
        ) : query.isError ? (
          <ErrorState compact onRetry={query.refetch} />
        ) : items.length === 0 ? (
          <EmptyState
            compact
            icon={list.filtering ? MagnifyingGlassIcon : ClipboardTextIcon}
            title={
              list.filtering
                ? t("submissions.list.no_results")
                : t("submissions.list.empty")
            }
          />
        ) : (
          <>
            {items.map((qualification, index) => (
              <Fragment key={qualification.id}>
                {byDate &&
                  (index === 0 ||
                    submittedAt(qualification).toDateString() !==
                      submittedAt(items[index - 1]).toDateString()) && (
                    <DayHeader date={submittedAt(qualification)} />
                  )}
                <SubmissionListItem
                  qualification={qualification}
                  selected={qualification.id === selectedId}
                  checked={checked.has(qualification.id)}
                  onSelect={onSelect}
                  onCheck={list.check}
                  showDay={!byDate}
                />
              </Fragment>
            ))}
            {list.hasMore && (
              <div className="p-3">
                <Button
                  variant="outline"
                  className="w-full"
                  disabled={query.isFetching}
                  onClick={list.loadMore}
                >
                  {query.isFetching
                    ? t("submissions.list.loading")
                    : t("submissions.list.load_more")}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * What to do with the checked rows, in one line of a 24rem pane: the shared
 * `BulkActionsBar` lays its labels out for a full-width table and took three.
 */
function SelectionBar({
  rows,
  actions,
  onClear,
}: {
  rows: Qualification[];
  actions: BulkAction<Qualification>[];
  onClear: () => void;
}) {
  const { t } = useTranslation("qualifications");
  const [running, setRunning] = useState<string | null>(null);
  const visible = actions.filter((action) => action.isVisible?.(rows) ?? true);

  const runAction = async (action: BulkAction<Qualification>) => {
    setRunning(action.key);
    try {
      await action.onAction(rows);
      onClear();
    } finally {
      setRunning(null);
    }
  };

  return (
    <div className="flex items-center gap-1 border-b bg-primary/5 py-1.5 pr-2 pl-3">
      <span className="flex-1 text-sm font-medium">
        {t("submissions.list.selected", { count: rows.length })}
      </span>
      {visible.map((action) => (
        <Tooltip key={action.key}>
          <TooltipTrigger asChild>
            <Button
              size="icon"
              variant={action.variant === "destructive" ? "ghost" : "outline"}
              className={cn(
                "size-8",
                action.variant === "destructive" && "text-destructive",
              )}
              aria-label={action.label}
              disabled={running !== null}
              confirm={action.confirm?.(rows)}
              onClick={() => runAction(action)}
            >
              {action.icon}
            </Button>
          </TooltipTrigger>
          <TooltipContent>{action.label}</TooltipContent>
        </Tooltip>
      ))}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            size="icon"
            variant="ghost"
            className="size-8"
            aria-label={t("submissions.list.clear_selection")}
            onClick={onClear}
            disabled={running !== null}
          >
            <XIcon className="size-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t("submissions.list.clear_selection")}</TooltipContent>
      </Tooltip>
    </div>
  );
}

/** "Aujourd'hui", "Hier", then the date — the call history's day separators. */
function DayHeader({ date }: { date: Date }) {
  const { t } = useTranslation("qualifications");
  const locale = useDateFnsLocale();
  const label = isToday(date)
    ? t("submissions.list.today")
    : isYesterday(date)
      ? t("submissions.list.yesterday")
      : format(date, isThisYear(date) ? "EEEE d MMMM" : "EEEE d MMMM yyyy", {
          locale,
        });

  return (
    <div className="sticky top-0 z-10 border-b bg-muted px-3 py-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
      {label}
    </div>
  );
}
